import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { getApps, initializeApp } from "firebase/app";
import { Auth, confirmPasswordReset, getAuth, verifyPasswordResetCode } from "firebase/auth";
import { IoAlertCircleOutline, IoCheckmarkCircleOutline } from "react-icons/io5";
import { auth } from "../../config/firebase";
import Wordmark from "../../components/common/Wordmark";
import PasswordField from "../../components/auth/PasswordField";
import PasswordRequirements, { usePasswordValidation } from "../../components/auth/PasswordRequirements";

const AlertIcon = IoAlertCircleOutline as any;
const CheckIcon = IoCheckmarkCircleOutline as any;

type Stage = "verifying" | "form" | "done" | "invalid";

// Links carry the apiKey of the Firebase project that issued them (like Firebase's own
// handler expects), which may not be the project this build signs in to — e.g. dev
// users-api links opened on a build configured for the delivery project.
const authForApiKey = (apiKey: string | null): Auth => {
  if (!apiKey || apiKey === auth.app.options.apiKey) return auth;
  const name = `auth-action-${apiKey}`;
  return getAuth(getApps().find((a) => a.name === name) ?? initializeApp({ apiKey }, name));
};

/**
 * Email action handler for password links (invite "Create your password" and
 * "Reset your password" emails). Replaces Firebase's stock /__/auth/action page
 * so we can show the password rules while the user types.
 */
const AuthActionPage: React.FC = () => {
  const [params] = useSearchParams();
  const mode = params.get("mode");
  const oobCode = params.get("oobCode") ?? "";
  const apiKey = params.get("apiKey");
  const linkAuth = useMemo(() => authForApiKey(apiKey), [apiKey]);

  const [stage, setStage] = useState<Stage>("verifying");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const { status, unavailable } = usePasswordValidation(password, linkAuth);
  const meetsPolicy = unavailable || !!status?.isValid;

  useEffect(() => {
    if (mode !== "resetPassword" || !oobCode) {
      setStage("invalid");
      return;
    }
    verifyPasswordResetCode(linkAuth, oobCode)
      .then((addr) => {
        setEmail(addr);
        setStage("form");
      })
      .catch(() => setStage("invalid"));
  }, [mode, oobCode, linkAuth]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!meetsPolicy) {
      setError("Your password doesn't meet all the requirements below.");
      return;
    }
    if (password !== confirm) {
      setError("The passwords don't match.");
      return;
    }
    try {
      setSaving(true);
      await confirmPasswordReset(linkAuth, oobCode, password);
      setStage("done");
    } catch (err: any) {
      console.error("Password reset failed:", err);
      if (err?.code === "auth/expired-action-code" || err?.code === "auth/invalid-action-code") {
        setStage("invalid");
      } else if (err?.code === "auth/password-does-not-meet-requirements") {
        setError("Your password doesn't meet all the requirements below.");
      } else {
        setError("Couldn't save your password. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-paper-200 p-6 sm:p-12">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <Wordmark className="text-3xl" />
        </div>

        <div className="bg-surface rounded-3xl shadow-card p-8 sm:p-10 border border-paper-300/70">
          {stage === "verifying" && (
            <div className="flex flex-col items-center gap-4 py-6">
              <div className="tf-spinner tf-spinner-lg" />
              <p className="text-sm text-ink-muted">Checking your link…</p>
            </div>
          )}

          {stage === "invalid" && (
            <>
              <h2 className="font-display text-3xl font-bold text-ink mb-2">Link expired</h2>
              <p className="text-ink-muted mb-6">
                This link has expired or has already been used. Request a new one from the sign-in
                page with <span className="font-medium">Forgot password?</span>
              </p>
              <Link
                to="/login"
                className="block w-full rounded-xl bg-brand-500 px-4 py-3.5 text-center text-base font-semibold text-on-brand hover:bg-brand-600 transition-colors"
              >
                Go to sign in
              </Link>
            </>
          )}

          {stage === "done" && (
            <>
              <div className="mb-6 flex items-start gap-2 rounded-xl bg-brand-50 border border-brand-500/30 px-4 py-3 text-sm text-brand-700">
                <CheckIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <span>Your password has been saved.</span>
              </div>
              <Link
                to="/login"
                className="block w-full rounded-xl bg-brand-500 px-4 py-3.5 text-center text-base font-semibold text-on-brand hover:bg-brand-600 transition-colors"
              >
                Sign in
              </Link>
            </>
          )}

          {stage === "form" && (
            <>
              <div className="mb-8">
                <h2 className="font-display text-3xl font-bold text-ink mb-2">Set your password</h2>
                <p className="text-ink-muted">
                  For <span className="font-medium text-ink-soft">{email}</span>
                </p>
              </div>

              {error && (
                <div className="mb-6 flex items-start gap-2 rounded-xl bg-bad-soft border border-bad-line px-4 py-3 text-sm text-bad-ink animate-fade-in">
                  <AlertIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Lets password managers save the new password against the right account. */}
                <input type="email" value={email} autoComplete="username" readOnly hidden />
                <PasswordField
                  id="new-password"
                  label="New password"
                  value={password}
                  onChange={setPassword}
                  autoComplete="new-password"
                  disabled={saving}
                  autoFocus
                />
                <PasswordRequirements status={status} />
                <PasswordField
                  id="confirm-password"
                  label="Confirm new password"
                  value={confirm}
                  onChange={setConfirm}
                  autoComplete="new-password"
                  disabled={saving}
                />
                <button
                  type="submit"
                  disabled={saving || !meetsPolicy || !confirm}
                  className="w-full rounded-xl bg-brand-500 px-4 py-3.5 text-base font-semibold text-on-brand shadow-brand-glow transition-all duration-300 hover:bg-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? "Saving…" : "Save password"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthActionPage;
